import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ExternalLink, Loader2, Phone, Calendar as CalendarIcon, MapPin } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { format, parseISO } from "date-fns";
import { PlacesAutocompleteInput } from "@/components/maps/PlacesAutocompleteInput";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { MapPicker } from "@/components/maps/MapPicker";

function mapsUrl(args: { placeId?: string | null; address?: string | null; lat?: any; lng?: any }) {
  const placeId = args.placeId ? String(args.placeId) : "";
  if (placeId) return `https://www.google.com/maps/search/?api=1&query_place_id=${encodeURIComponent(placeId)}`;
  const lat = args.lat != null ? Number(args.lat) : null;
  const lng = args.lng != null ? Number(args.lng) : null;
  if (lat != null && lng != null && !Number.isNaN(lat) && !Number.isNaN(lng)) {
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${lat},${lng}`)}`;
  }
  const address = args.address ? String(args.address) : "";
  if (address) return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
  return "";
}

export default function CateringRequestPage() {
  const { businessSlug } = useParams();

  const [submitting, setSubmitting] = useState(false);
  const [doneId, setDoneId] = useState<string | null>(null);
  const [bizLoading, setBizLoading] = useState(true);
  const [biz, setBiz] = useState<any>(null);

  const [form, setForm] = useState({
    event_date: "",
    attendees: "0",
    budget_amount: "",
    service_style: "" as "" | "buffet" | "packed",
    contact_name: "",
    contact_phone: "",
    contact_email: "",
    location: "",
    location_place_id: "",
    location_lat: null as number | null,
    location_lng: null as number | null,
    notes: "",
    company_name: "",
  });
  const [locationOpen, setLocationOpen] = useState(false);
  const eventDateSelected = useMemo(() => {
    if (!form.event_date) return undefined;
    try {
      return parseISO(form.event_date);
    } catch {
      return undefined;
    }
  }, [form.event_date]);

  const canSubmit = useMemo(() => {
    if (!businessSlug) return false;
    if (!form.contact_name.trim()) return false;
    return true;
  }, [businessSlug, form.contact_name]);

  useEffect(() => {
    const run = async () => {
      if (!businessSlug) return;
      setBizLoading(true);
      try {
        const { data, error } = await (supabase as any).rpc("get_public_business_branding", {
          _business_slug: businessSlug,
        });
        if (error) throw error;
        setBiz((data && data[0]) || null);
      } catch {
        setBiz(null);
      } finally {
        setBizLoading(false);
      }
    };
    run();
  }, [businessSlug]);

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const attendees = Math.max(0, Number(form.attendees || 0));
      const budget = form.budget_amount.trim() ? Number(form.budget_amount) : null;
      const serviceStyle = form.service_style || "buffet";
      const eventDate = form.event_date ? form.event_date : null;

      const { data, error } = await (supabase as any).rpc("submit_catering_request", {
        _business_slug: businessSlug,
        _company_name: form.company_name.trim() || null,
        _contact_name: form.contact_name.trim(),
        _contact_phone: form.contact_phone.trim() || null,
        _contact_email: form.contact_email.trim() || null,
        _event_date: eventDate,
        _attendees: attendees,
        _budget_amount: budget,
        _service_style: serviceStyle,
        _location: form.location.trim() || null,
        _notes: form.notes.trim() || null,
        _location_place_id: form.location_place_id.trim() || null,
        _location_lat: typeof form.location_lat === "number" ? form.location_lat : null,
        _location_lng: typeof form.location_lng === "number" ? form.location_lng : null,
      });
      if (error) throw error;
      const requestId = data?.[0]?.request_id;
      setDoneId(String(requestId || "ok"));
      if (requestId) {
        supabase.functions.invoke("send-email", {
          body: { type: "catering_request_created", requestId: String(requestId) },
        }).catch(() => {});
      }
    } catch (e: any) {
      alert(e?.message || "Failed to submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (doneId) {
    return (
      <div className="min-h-screen bg-background p-4">
        <div className="mx-auto max-w-xl">
          <Card className="rounded-2xl">
            <CardContent className="p-6 space-y-2">
              <div className="text-lg font-semibold">Request submitted</div>
              <div className="text-sm text-muted-foreground">
                Thanks, we’ve received your catering request. We’ll reach out shortly.
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (!bizLoading && biz && (biz as any).event_catering_requests_enabled !== true) {
    return (
      <div className="min-h-screen bg-background p-4 flex items-center justify-center">
        <div className="mx-auto max-w-xl w-full">
          <Card className="rounded-2xl border border-border">
            <CardContent className="p-6 space-y-2">
              <div className="text-lg font-semibold">Request form not available</div>
              <div className="text-sm text-muted-foreground">
                This business is not currently accepting event or catering requests through this form.
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background px-4 py-10">
      <div className="mx-auto max-w-6xl grid gap-8 lg:grid-cols-2 items-start">
        <div className="space-y-5">
          {bizLoading ? (
            <div className="h-10 w-56 rounded-lg bg-muted animate-pulse" />
          ) : biz?.business_name ? (
            <div className="flex items-center gap-3">
              {biz.business_logo_url ? (
                <img
                  src={String(biz.business_logo_url)}
                  alt={`${biz.business_name} logo`}
                  className="h-10 w-10 rounded-xl object-cover border border-border bg-background"
                />
              ) : (
                <div className="h-10 w-10 rounded-xl bg-primary/10 flex items-center justify-center font-semibold text-primary">
                  {String(biz.business_name).slice(0, 1).toUpperCase()}
                </div>
              )}
              <div className="min-w-0">
                <div className="font-semibold text-foreground truncate">{biz.business_name}</div>
                <div className="text-xs text-muted-foreground truncate">Catering request</div>
              </div>
            </div>
          ) : null}

          <div className="text-5xl sm:text-6xl font-extrabold tracking-tight text-foreground leading-[0.95]">
            Tell us about
            <br />
            your event
          </div>
          <p className="text-base sm:text-lg text-muted-foreground max-w-xl">
            Fill out our simple request form with the details of your event, including the number of attendees, dietary
            preferences, and any special requests.
          </p>

          {bizLoading ? null : biz ? (
            <div className="flex flex-col gap-2 text-sm text-muted-foreground">
              {biz.business_phone ? (
                <div className="inline-flex items-center gap-2">
                  <Phone className="h-4 w-4" />
                  <span>{biz.business_phone}</span>
                </div>
              ) : null}
              {(() => {
                const url = mapsUrl({
                  placeId: biz.location_place_id,
                  address: biz.location_address,
                  lat: biz.location_lat,
                  lng: biz.location_lng,
                });
                if (!url) return null;
                return (
                  <a
                    href={url}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-2 text-sm font-medium text-primary hover:underline w-fit"
                  >
                    <ExternalLink className="h-4 w-4" />
                    Location
                  </a>
                );
              })()}
            </div>
          ) : null}
        </div>

        <Card className="rounded-2xl shadow-soft border border-border bg-background">
          <CardContent className="p-6">
            <div className="grid gap-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label>Date of event</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full justify-between">
                        <span className={form.event_date ? "text-foreground" : "text-muted-foreground"}>
                          {form.event_date ? format(eventDateSelected as any, "MMM d, yyyy") : "Select a date"}
                        </span>
                        <CalendarIcon className="h-4 w-4 text-muted-foreground" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={eventDateSelected}
                        onSelect={(d) => {
                          if (!d) {
                            setForm((f) => ({ ...f, event_date: "" }));
                            return;
                          }
                          setForm((f) => ({ ...f, event_date: format(d, "yyyy-MM-dd") }));
                        }}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <div className="space-y-1.5">
                  <Label>Number of attendees</Label>
                  <Input
                    type="number"
                    min={0}
                    value={form.attendees}
                    onChange={(e) => setForm((f) => ({ ...f, attendees: e.target.value }))}
                  />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label>Budget</Label>
                  <Input
                    type="number"
                    min={0}
                    value={form.budget_amount}
                    onChange={(e) => setForm((f) => ({ ...f, budget_amount: e.target.value }))}
                    placeholder="e.g., 1500"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>Buffet or packed?</Label>
                  <Select value={form.service_style} onValueChange={(v: any) => setForm((f) => ({ ...f, service_style: v }))}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="buffet">Buffet</SelectItem>
                      <SelectItem value="packed">Packed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label>Your name</Label>
                  <Input
                    value={form.contact_name}
                    onChange={(e) => setForm((f) => ({ ...f, contact_name: e.target.value }))}
                    placeholder="Your name"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>Phone number</Label>
                  <Input
                    value={form.contact_phone}
                    onChange={(e) => setForm((f) => ({ ...f, contact_phone: e.target.value }))}
                    placeholder="Your phone number"
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label>Email address</Label>
                <Input
                  type="email"
                  value={form.contact_email}
                  onChange={(e) => setForm((f) => ({ ...f, contact_email: e.target.value }))}
                  placeholder="name@company.com"
                />
              </div>

              <div className="space-y-1.5">
                <div className="flex items-center justify-between gap-3">
                  <Label>Location of the event</Label>
                  {(() => {
                    const url = mapsUrl({
                      placeId: form.location_place_id,
                      address: form.location,
                      lat: form.location_lat,
                      lng: form.location_lng,
                    });
                    if (!url) return null;
                    return (
                      <a
                        href={url}
                        target="_blank"
                        rel="noreferrer"
                        className="text-xs font-medium text-primary hover:underline"
                      >
                        Open in Maps
                      </a>
                    );
                  })()}
                </div>
                <div className="grid gap-2 sm:grid-cols-[1fr_auto] sm:items-start">
                  <PlacesAutocompleteInput
                    value={form.location}
                    onValueChange={(v) =>
                      setForm((f) => ({
                        ...f,
                        location: v,
                        location_place_id: "",
                        location_lat: null,
                        location_lng: null,
                      }))
                    }
                    onPlaceSelected={(p) =>
                      setForm((f) => ({
                        ...f,
                        location: p.formattedAddress,
                        location_place_id: p.placeId || "",
                        location_lat: typeof p.lat === "number" ? p.lat : null,
                        location_lng: typeof p.lng === "number" ? p.lng : null,
                      }))
                    }
                    placeholder="Search for an address"
                  />
                  <Button type="button" variant="outline" className="w-full sm:w-auto" onClick={() => setLocationOpen(true)}>
                    <MapPin className="h-4 w-4 mr-2" />
                    Pick on map
                  </Button>
                </div>
              </div>

              <div className="space-y-1.5">
                <Label>Additional notes</Label>
                <Textarea
                  value={form.notes}
                  onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                  placeholder="Share any extra information"
                  className="min-h-[120px]"
                />
              </div>

              <Button
                className="w-full rounded-xl"
                onClick={submit}
                disabled={!canSubmit || submitting}
              >
                {submitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  "Submit"
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      <Dialog modal={false} open={locationOpen} onOpenChange={setLocationOpen}>
        <DialogContent className="sm:max-w-[720px] w-[calc(100%-2rem)] max-h-[90vh] p-0 gap-0 overflow-hidden rounded-2xl flex flex-col">
          <DialogHeader className="px-6 pt-6 pb-4 flex-shrink-0">
            <DialogTitle>Pick a location</DialogTitle>
          </DialogHeader>
          <div className="overflow-y-auto px-6 pb-6 flex-1 min-h-0">
            <MapPicker
              address={form.location}
              onAddressChange={(v) =>
                setForm((f) => ({ ...f, location: v, location_place_id: "", location_lat: null, location_lng: null }))
              }
              lat={form.location_lat}
              lng={form.location_lng}
              onLatLngChange={(pos) => setForm((f) => ({ ...f, location_lat: pos?.lat ?? null, location_lng: pos?.lng ?? null }))}
              onPlaceSelected={(p) =>
                setForm((f) => ({
                  ...f,
                  location: p.formattedAddress,
                  location_place_id: p.placeId || "",
                  location_lat: typeof p.lat === "number" ? p.lat : null,
                  location_lng: typeof p.lng === "number" ? p.lng : null,
                }))
              }
            />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}


